/**
  * @class meleActorDopplerEvents
  * @description
  *
  * # Purpose
  * Implements `<mele-actor-dopplerevents>` custom web element for showing doppler radar measurements
  */

import meleActor from 'meleActor'
const Algo = require('../../msys/msysAV/src_em/algo.js')
const Draw = require('../../msys/msysAV/src_em/draw.js')
const Doppler = require('../../msvc/msvcDoppler/doppler')

const SVG_ARROWS = require('../../res/icons/lanes.js')

const PROPERTIES = _.extend({}, {
  position_display: { description: 'how to display vehicle position', type: 'string', default: 'cornersline', enum: ['corners', 'line', 'cornersline'] },
  clean_hit: { description: 'seconds to display hit events', type: 'number', default: 120 },
  clean_valid: { description: 'seconds to display valid events', type: 'number', default: 30 },
  clean_dry: { description: 'seconds to display test events', type: 'number', default: 30 },
  clean_simulator: { description: 'seconds to display simulator events', type: 'number', default: 600 },
  clean_ignored: { description: 'seconds to display ignored events', type: 'number', default: 0 },
}, meleActor._properties)

export default class meleActorDopplerEvents extends meleActor {
  init() {
    super.init()
  } // init

  play() {
    this.innerHTML = ''
  } // play

  message({ action, params }) {
    if(action==='mmDopplerEvent' && params.channel===parseInt(this.parentElement.ch)) {
      this.mmDopplerEvent(params)
    }
  } // message

  async mmDopplerEvent(params) {
    const { result, event_dt, event, jpeg3, alpr, calibration, optics } = params
    params.rig = Algo.rig(calibration, optics)
    const { direction, speed, lane, fix_longitudal } = event
    if(!(result==='hit' || result==='valid' || result==='dry' || result==='simulator' || result==='ignored')) return
    const clean = this['clean_'+result]
    if(!(clean>0)) return
    const speed_kmh = Math.floor(Math.abs(speed)*3.6)
    const arrows = SVG_ARROWS[direction+Math.min(3, lane)] || ''
    let bg = Doppler.background(result)
    if(result==='hit' && isNaN(fix_longitudal)) {
      bg = Doppler.LEGEND_COLORS.hitweak
    }
    const fg = Doppler.foreground(result)
    const html = `
      <canvas width=200 height=100></canvas>
      <svg viewBox="0 0 100 100" style="user-select: none">
        <circle cx="50" cy="50" r="50" fill="${bg}" />
        <path d="${SVG_ARROWS[123]}" fill="none" stroke="${fg}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="5,5"/>
        <path d="${arrows}" fill="none" stroke="${fg}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
        <text x="50" y="60" text-anchor="middle" dominant-baseline="middle" fill="${fg}" stroke="${fg}" stroke-width="1" font-family="Verdana, sans-serif" font-size="40">${speed_kmh}</text>
      </svg>
      <div>${alpr?.lpn??''}</div>
    `
    const el = this.prependChildren({
      tag: 'dopplerevent',
      key: event_dt,
      result,
      html,
    })

    el.params = params // save params inside element
    el.addEventListener('click', this.showDetails.bind(this, el))

    delete el.params.jpeg2 // not needed; delete to save memory

    const canvas = el.find('canvas')
    const ctx = canvas.getContext('2d')
    const preview_img = await Draw.imgFromUint8Array(jpeg3)
    delete el.params.jpeg3 // not needed anymore; delete to save memory
    if(preview_img) ctx.drawImage(preview_img, 0, 0, canvas.width, canvas.height)

    // in manual mode, automatically show detail of hit
    if(result==='hit' && params.limits.mode==='manual') this.showDetails(el)

    setTimeout(()=>el.deleteElement(), clean*1000)
  } // mmDopplerEvent

  async showDetails(el) {
    const { result, event_dt, seq, limits, event, jpeg_us } = el.params
    const { direction_limit, lane_limit, speed_limit, hit_limit } = limits
    const { duration, direction, min_distance, lateral, lane, travelled, sog_speed, speed } = event
    const elModal = this.modalOpen(require('./meleActorDopplerEvents_detail.json'))
    elModal.datakey = event_dt
    const values = {
      result: `{{doppler.result.${result}}}${seq>0 ? ` (# ${seq})` : ''}`,
      timestamp: _.usec2ymdhmsms(jpeg_us || event_dt),
      duration: duration.toFixed(2),
      sog_speed: (Math.floor(Math.abs(sog_speed)*3.6*10)/10).toFixed(1),
      speed: (Math.floor(Math.abs(speed)*3.6*10)/10).toFixed(1),
      speed_hit_limit: `${speed_limit} (${hit_limit})`,
      direction,
      direction_limit,
      travelled: Math.abs(travelled).toFixed(1),
      distance_lateral: min_distance.toFixed(1) + ' / ' + lateral.toFixed(1),
      lane,
      lane_limit,
    }
    elModal.setValues(values)
    elModal.classList.add('result_'+result)

    // setup TEST watermark
    elModal.find('[key=test] div').innerHTML = result==='dry' || result==='simulator' ? this.translate('doppler.TEST') : ''

    // draw icon with lane/direction/speed
    const speed_kmh = Math.floor(Math.abs(speed)*3.6)
    const arrows = SVG_ARROWS[direction+Math.min(3, lane)] || ''
    const color = result==='hit' ? 'var(--red)' : result==='valid' ? 'var(--green)' : '#666666'

    elModal.find('@icon').innerHTML = `
      <svg viewBox="0 0 100 100" style="user-select: none">
        <circle cx="50" cy="50" r="50" fill="${color}" />
        <path d="${SVG_ARROWS[123]}" fill="none" stroke="#ffffffaa" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="5,5"/>
        <path d="${arrows}" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
        <text x="50" y="60" text-anchor="middle" dominant-baseline="middle" fill="white" stroke="white" stroke-width="1" font-family="Verdana, sans-serif" font-size="40">${speed_kmh}</text>
      </svg>
    `

    const img = elModal.find('img')
    const alpr_holder = elModal.find('alprs')

    img.src = ''
    alpr_holder.innerHTML = ''

    // setup image
    if(!('doppler_img_src' in el)) {
      // for the first time, transform jpeg uint8array into img element
      if(el.params.jpeg) {
        const artefacts = {
          mid: true,
          alpr: true,
        }
        if(result==='dry' || result==='simulator') {
          artefacts.corners = true
          artefacts.travel = true
        }else {
          const { position_display } = this
          if(/corners/.test(position_display)) {
            artefacts.corners = true
          }
          if(/line/.test(position_display)) {
            artefacts.travel = true
          }
        }
        if(document.body.getAttribute('mode')==='admin') {
          artefacts.longitudal_diff = true
        }
        el.doppler_img_src = (await Doppler.getEventImage({ params: el.params }, artefacts))?.src
        delete el.params.jpeg // conserve memory
      }else {
        el.doppler_img_src = ''
      }
    }

    img.src = el.doppler_img_src

    // draw ALPR once the image is loaded
    img.onload = () => {
      const elLPN = elModal.find('@lpn')
      const elLP = elModal.find('@lp')
      if(el.params.alpr?.lpn) {
        const { lpn, country, base64 } = el.params.alpr
        elLPN.innerHTML = lpn ? `${lpn} (${country||'?'})` : this.translate('doppler.unrecognised')
        elLP.src = base64 ? 'data:image/jpeg;base64,'+base64 : ''
      }else {
        elLPN.innerHTML = this.translate('doppler.unrecognised')
        elLP.src = ''
      }
    }

    // setup list of ALPR results
    const alpr_table = await this.constructMosaicTable(el.params?.alpr)
    if(alpr_table) {
      alpr_holder.appendChild(alpr_table)
    }
  } // showDetails

  async constructMosaicTable(alpr) {
    if(!alpr || typeof alpr!=='object') return
    let { mosaic, detections } = alpr
    if(!mosaic || typeof mosaic!=='object') return
    if(!detections || !Array.isArray(detections)) detections = []

    const resize = parseFloat(mosaic?.resize)
    if(!(resize>=1)) return
    const jpeg = mosaic?.jpeg
    if(!jpeg) return

    // draw mosaic image onto canvas
    const canvas = document.createElement('canvas')
    await Draw.jpegUint8ArrayToCanvas(canvas, mosaic.jpeg)
    if(!(canvas.width>0 && canvas.height>0)) return

    // draw detection rectangles into the mosaic image
    for(const detection of detections) {
      const { tl, tr, bl, br, result } = detection.mosaic_pos
      const points = [tl, tr, br, bl, tl].map(([x, y])=>[Math.round(x/resize), Math.round(y/resize)])
      const color = result==='best' ? 'lime' : result==='good' ? 'yellow' : result==='group' ? 'orange' : 'magenta'
      Draw.drawPolyline(canvas, points, color, 4/resize)
    }

    // now extract each tile as separate image
    const table = document.createElement('table')
    const { tiles_rows, tiles_columns, tile_width, tile_height, tile_margin } = mosaic
    const canvas2 = document.createElement('canvas')
    canvas2.width = Math.round(tile_width/resize)
    canvas2.height = Math.round(tile_height/resize)
    const ctx2 = canvas2.getContext('2d')
    let tile_idx = 0
    for(let row=0; row<tiles_rows; row++) {
      const y = Math.round(row*(tile_height+tile_margin)/resize)
      for(let col=0; col<tiles_columns; col++, tile_idx++) {
        let table_row = document.createElement('tr')
        table.appendChild(table_row)

        // extract tile image from mosaic and display it in table cell
        let td = null
        td = document.createElement('td')
        if(canvas) {
          td.className = 'tile'
          const x = Math.round(col*(tile_width+tile_margin)/resize)
          ctx2.drawImage(canvas, x, y, tile_width, tile_height, 0, 0, tile_width, tile_height)
          const dataURI = canvas2.toDataURL('image/jpeg', 1.0)
          const img = document.createElement('img')
          img.src = dataURI
          td.appendChild(img)
        }else {
          td.className = 'tile empty'
        }
        const div = document.createElement('div')
        const tile = mosaic.tiles?.[tile_idx]
        div.className = 'time_offset'
        div.innerHTML = tile ? Math.round(tile.shot_offset/1000) + ' ms' : '?'
        td.appendChild(div)
        if(tile?.shot_offset<0) {
          td.classList.add('before')
        }else if(tile?.shot_offset>0) {
          td.classList.add('after')
        }else {
          td.classList.add('shot')
        }

        table_row.appendChild(td)

        // display detections as rows below the tile image
        let tile_detections = detections.filter(({ tile_row, tile_col })=>tile_row===row && tile_col===col)
        if(!tile_detections.length) {
          const td2 = document.createElement('td')
          td2.className = 'lpn'
          table_row.appendChild(td2)
          const td3 = document.createElement('td')
          td3.className = 'meta'
          table_row.appendChild(td3)
          td3.innerHTML = `
              <lpn>${this.translate('doppler.unrecognised')}</lpn>
            `
          continue
        }
        tile_detections = tile_detections.sort((a, b)=>b.score-a.score)
        if(td && tile_detections.length>1) {
          td.rowSpan = tile_detections.length
        }

        let first = true
        for(const detection of tile_detections) {
          if(first) {
            first = false
          }else {
            table_row = document.createElement('tr')
            table.appendChild(table_row)
          }

          let { mosaic_pos, base64, lpn, score, confidence, lpn_confidence, country, result, group_result, ocr, reason, occlusion, truncated, obstructed, unreadable, group } = detection
          const { tl, tr, bl, br } = mosaic_pos
          const w = Math.round((tr[0]-tl[0]+br[0]-bl[0])/2)
          const h = Math.round((bl[1]-tl[1]+br[1]-tr[1])/2)
          lpn = String(lpn??'')
          country = String(country??'')
          lpn_confidence = _.natural(lpn_confidence)

          const td2 = document.createElement('td')
          td2.className = 'lpn '+result
          table_row.appendChild(td2)
          td2.innerHTML = `<img src="data:image/jpeg;base64,${base64}" />`

          const td3 = document.createElement('td')
          td3.className = 'meta '+result+(detection.score>0 ? '' : ' noscore')
          table_row.appendChild(td3)
          const diff = detection.longitudal_diff
          td3.innerHTML = `
              <lpn>${lpn} / ${country} (group ${group})</lpn>
              <confidence>score <b>${score}</b>: ${confidence}%/${lpn_confidence}% (${w}x${h} px)</confidence>
              <extra><b>${ocr}${reason?':'+reason:''}</b> occ=${occlusion} trun=${truncated} obstr=${obstructed} unread=${unreadable}</extra>
              <extra>${result}/${group_result} ${isNaN(diff) ? '' : `longitudal diff = <b>${diff.toFixed(2)}</b> m`}</extra>
          `
        }
      }
    }

    return table
  } // constructMosaicTable
} // class meleActorDopplerEvents

meleActorDopplerEvents._properties = PROPERTIES
meleActorDopplerEvents.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-actor-dopplerevents', meleActorDopplerEvents)
